import React from 'react';
import { Card, Row, Col, Space, Typography, DatePicker, Select, Button, Dropdown, Menu, Tooltip, Alert, Input, Grid } from 'antd';
import {
    SearchOutlined,
    PlusOutlined,
    UploadOutlined,
    DownloadOutlined,
    FileExcelOutlined,
    FileTextOutlined,
    HistoryOutlined,
    InfoCircleOutlined,
    TagsOutlined,
    PieChartOutlined
} from '@ant-design/icons';
import { Landmark, AlertCircle, HelpCircle, Tags, TrendingUp, TrendingDown, Plus, List, Sparkles, CalendarClock } from 'lucide-react';
import { Badge } from 'antd';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

export default function MovimentacaoFinanceiraHeader({
    period,
    setPeriod,
    bankAccount,
    setBankAccount,
    bankAccounts = [],
    onSearch,
    onNewRecebimento,
    onNewPagamento,
    onImport,
    onExport,
    onOpenFinance,
    onOpenFinanceAviso,
    onNewAccount,
    onManageAccounts,
    onReclassificar,
    onShowInstrucoes,
    onShowDashboard,
    onShowRules,
    onShowWhatsNew,
    whatsNewBadge,
    filterPendencias,
    setFilterPendencias,
    hasPendencias,
    filterSituacao,
    setFilterSituacao,
    filterTipo,
    setFilterTipo,
    filterCategoria,
    setFilterCategoria,
    filterTexto,
    setFilterTexto,
    availableCategorias = [],
    isContador,
    selectedCount
}) {
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    const exportMenu = (
        <Menu onClick={({ key }) => onExport(key)} className="premium-dropdown-menu">
            <Menu.Item key="extrato" icon={<FileTextOutlined style={{ color: '#2563eb' }} />}>Extrato de Movimentações</Menu.Item>
            <Menu.Item key="excel" icon={<FileExcelOutlined style={{ color: '#16a34a' }} />}>Exportar Excel</Menu.Item>
            {isContador && (
                <Menu.Item key="excel-dominio" icon={<FileExcelOutlined style={{ color: '#059669' }} />}>Excel Domínio (Contabilidade)</Menu.Item>
            )}
            <Menu.Item key="csv" icon={<FileTextOutlined style={{ color: '#64748b' }} />}>Exportar CSV</Menu.Item>
            <Menu.Item key="ofx" icon={<FileTextOutlined style={{ color: '#2563eb' }} />}>Exportar OFX</Menu.Item>
        </Menu>
    );

    const importMenu = (
        <Menu onClick={({ key }) => onImport(key)} className="premium-dropdown-menu">
            <Menu.Item key="ofx" icon={<UploadOutlined style={{ color: '#2563eb' }} />}>Importar OFX</Menu.Item>
            <Menu.Item key="excel" icon={<FileExcelOutlined style={{ color: '#16a34a' }} />}>Importar Excel</Menu.Item>
        </Menu>
    );

    const configMenu = (
        <Menu className="premium-dropdown-menu">
            <Menu.Item key="open-finance" icon={<Landmark size={16} />} onClick={onOpenFinance}>Open Finance</Menu.Item>
            <Menu.Item key="open-finance-aviso" icon={<CalendarClock size={16} />} onClick={onOpenFinanceAviso}>Como funciona o Open Finance</Menu.Item>
            <Menu.Item key="manage-accounts" icon={<List size={16} />} onClick={onManageAccounts}>Gerenciar Contas</Menu.Item>
            <Menu.Divider />
            {isContador && (
                <>
                    {/* Regras de Classificação — ocultado por ora (não funciona com a nova
                        integração Pantheon). Reativar quando as regras rodarem no Pantheon. */}
                    {/* <Menu.Item key="rules" icon={<Sparkles size={16} />} onClick={onShowRules}>Regras de Classificação</Menu.Item> */}
                    <Menu.Item key="new-account" icon={<Plus size={16} />} onClick={onNewAccount}>Cadastrar Nova Conta</Menu.Item>
                </>
            )}
        </Menu>
    );

    return (
        <Card className="premium-card" style={{ marginBottom: 24, borderRadius: '16px', border: 'none', boxShadow: '0 4px 20px -5px rgba(0,0,0,0.05)' }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'flex-start',
                flexDirection: isMobile ? 'column' : 'row',
                gap: 16
            }}>
                <Space direction="vertical" size={0}>
                    <Title level={4} style={{ margin: 0, color: '#0f172a', fontWeight: 800, fontSize: isMobile ? '18px' : '22px' }}>
                        Movimentação Financeira
                    </Title>
                    <Text type="secondary" style={{ fontSize: '13px', color: '#64748b' }}>
                        Controle suas receitas e despesas com conciliação automática.
                    </Text>
                </Space>

                <div style={{
                    display: 'flex',
                    gap: 8,
                    flexWrap: 'wrap',
                    justifyContent: isMobile ? 'space-between' : 'flex-end',
                    width: isMobile ? '100%' : 'auto'
                }}>
                    <Space size={8} wrap style={{ width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
                        {(hasPendencias || filterPendencias) && (
                            <Badge dot={!filterPendencias} status="error">
                                <Button
                                    icon={<AlertCircle size={16} />}
                                    onClick={() => setFilterPendencias(!filterPendencias)}
                                    style={{
                                        borderRadius: '10px',
                                        height: '40px',
                                        background: filterPendencias ? '#fee2e2' : '#ffffff',
                                        borderColor: filterPendencias ? '#fecaca' : '#e2e8f0',
                                        color: filterPendencias ? '#dc2626' : '#64748b',
                                        fontWeight: 600
                                    }}
                                >
                                    {!isMobile && (filterPendencias ? 'Ver Tudo' : 'Pendências')}
                                </Button>
                            </Badge>
                        )}

                        <Dropdown overlay={configMenu} placement="bottomRight">
                            <Button
                                icon={<Landmark size={16} />}
                                style={{ borderRadius: '10px', height: '40px', fontWeight: 600, color: '#64748b' }}
                            >
                                {!isMobile && 'Contas'}
                            </Button>
                        </Dropdown>

                        <Tooltip title="Dashboard">
                            <Button
                                icon={<PieChartOutlined style={{ fontSize: '18px' }} />}
                                onClick={onShowDashboard}
                                style={{ borderRadius: '10px', height: '40px', color: '#64748b' }}
                            />
                        </Tooltip>

                        <Tooltip title="Novidades do Portal">
                            <Badge dot={whatsNewBadge} offset={[-2, 2]}>
                                <Button
                                    icon={<Sparkles size={18} />}
                                    onClick={onShowWhatsNew}
                                    style={{
                                        borderRadius: '10px',
                                        height: '40px',
                                        color: '#7c3aed',
                                        background: '#f5f3ff',
                                        borderColor: '#ddd6fe'
                                    }}
                                />
                            </Badge>
                        </Tooltip>

                        <Button
                            type="primary"
                            icon={<TrendingUp size={16} />}
                            onClick={onNewRecebimento}
                            style={{
                                borderRadius: '10px',
                                height: '40px',
                                fontWeight: 700,
                                background: '#10b981',
                                borderColor: '#10b981',
                                flex: isMobile ? 1 : 'none'
                            }}
                        >
                            {!isMobile && 'Receita'}
                        </Button>
                        <Button
                            type="primary"
                            icon={<TrendingDown size={16} />}
                            onClick={onNewPagamento}
                            style={{
                                borderRadius: '10px',
                                height: '40px',
                                fontWeight: 700,
                                background: '#e11d48',
                                borderColor: '#e11d48',
                                flex: isMobile ? 1 : 'none'
                            }}
                        >
                            {!isMobile && 'Despesa'}
                        </Button>
                    </Space>
                </div>
            </div>

            <div style={{
                borderTop: '1px solid #f1f5f9',
                marginTop: 24,
                paddingTop: 24
            }}>
                <Row gutter={[16, 16]} align="bottom">
                    <Col xs={24} lg={10}>
                        <div style={{ marginBottom: 8, fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            O que você procura?
                        </div>
                        <Input
                            placeholder="Buscar descrição..."
                            value={filterTexto}
                            onChange={(e) => setFilterTexto(e.target.value)}
                            size="large"
                            allowClear
                            prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                            style={{ borderRadius: '10px', height: '45px', border: '1px solid #e2e8f0' }}
                        />
                    </Col>

                    <Col xs={24} md={12} lg={6}>
                        <div style={{ marginBottom: 8, fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Período
                        </div>
                        <DatePicker.RangePicker
                            format={["DD/MM/YYYY", "DDMMYYYY"]}
                            value={period}
                            onChange={setPeriod}
                            style={{ width: '100%', borderRadius: '10px', height: '45px', border: '1px solid #e2e8f0' }}
                            allowClear={false}
                            size="large"
                        />
                    </Col>

                    <Col xs={24} md={12} lg={5}>
                        <div style={{ marginBottom: 8, fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Fluxo
                        </div>
                        <div style={{
                            background: '#f1f5f9',
                            padding: '4px',
                            borderRadius: '12px',
                            display: 'flex',
                            height: '45px',
                            alignItems: 'center',
                            width: '100%'
                        }}>
                            {[
                                { label: 'Tudo', value: '', icon: <List size={14} /> },
                                { label: 'Rec.', value: 'C', icon: <TrendingUp size={14} color="#10b981" /> },
                                { label: 'Desp.', value: 'D', icon: <TrendingDown size={14} color="#e11d48" /> }
                            ].map(item => (
                                <button
                                    key={item.value}
                                    onClick={() => setFilterTipo(item.value)}
                                    style={{
                                        border: 'none',
                                        background: filterTipo === item.value ? '#ffffff' : 'transparent',
                                        color: filterTipo === item.value ? '#0f172a' : '#64748b',
                                        flex: 1,
                                        height: '35px',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 6,
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        transition: 'all 0.2s',
                                        boxShadow: filterTipo === item.value ? '0 2px 4px rgba(0,0,0,0.05)' : 'none'
                                    }}
                                >
                                    {item.icon}
                                    {item.label}
                                </button>
                            ))}
                        </div>
                    </Col>

                    <Col xs={24} lg={3}>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={onSearch}
                            size="large"
                            block
                            style={{
                                height: '45px',
                                borderRadius: '10px',
                                fontWeight: 700,
                                background: '#2563eb',
                                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)'
                            }}
                        >
                            Filtrar
                        </Button>
                    </Col>
                </Row>

                <div style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    flexWrap: 'wrap',
                    gap: 12,
                    marginTop: 16,
                    padding: '12px',
                    background: '#f8fafc',
                    borderRadius: '12px',
                    border: '1px dashed #e2e8f0'
                }}>
                    <Space direction={isMobile ? 'vertical' : 'horizontal'} size={12} split={!isMobile && <div style={{ width: 1, height: 14, background: '#e2e8f0' }} />} style={{ width: isMobile ? '100%' : 'auto' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: isMobile ? '100%' : 'auto' }}>
                            <Landmark size={14} color="#64748b" />
                            <Select
                                placeholder="Conta Bancária"
                                variant="borderless"
                                value={bankAccount}
                                onChange={setBankAccount}
                                style={{ width: isMobile ? '100%' : 150 }}
                                dropdownMatchSelectWidth={false}
                                options={[
                                    { value: '', label: 'Todas as Contas' },
                                    { value: '0', label: '000 - Caixa' },
                                    ...bankAccounts.map(acc => ({
                                        value: acc.id,
                                        // Conta "só banco" (planilha) não tem número → mostra só o banco.
                                        label: acc.conta
                                            ? `${acc.banco?.nome || 'Banco'} - CC: ${acc.conta}`
                                            : `${acc.banco?.nome || 'Banco'}`
                                    }))
                                ]}
                            />
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: isMobile ? '100%' : 'auto' }}>
                            <AlertCircle size={14} color="#64748b" />
                            <Select
                                placeholder="Situação"
                                variant="borderless"
                                value={filterSituacao}
                                onChange={setFilterSituacao}
                                style={{ width: isMobile ? '100%' : 150 }}
                                options={[
                                    { value: '', label: 'Todas as Situações' },
                                    { value: 'L', label: 'Liquidado' },
                                    { value: 'W', label: 'Aguardando Cliente' },
                                    { value: 'N', label: 'Não Identificado' },
                                    { value: 'C', label: 'Cancelado' }
                                ]}
                            />
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: isMobile ? '100%' : 'auto' }}>
                            <Tags size={14} color="#64748b" />
                            <Select
                                placeholder="Categoria"
                                variant="borderless"
                                value={filterCategoria}
                                onChange={setFilterCategoria}
                                style={{ width: isMobile ? '100%' : 150 }}
                                showSearch
                                options={[
                                    { value: '', label: 'Todas as Categorias' },
                                    ...availableCategorias.map(cat => ({
                                        value: cat.id,
                                        label: cat.nome
                                    }))
                                ]}
                            />
                        </div>
                    </Space>

                    <div style={{
                        marginLeft: isMobile ? 0 : 'auto',
                        display: 'flex',
                        justifyContent: isMobile ? 'space-between' : 'flex-end',
                        width: isMobile ? '100%' : 'auto',
                        paddingTop: isMobile ? 12 : 0,
                        borderTop: isMobile ? '1px solid #e2e8f0' : 'none'
                    }}>
                        <Dropdown overlay={importMenu} placement="bottomRight">
                            <Button type="text" icon={<UploadOutlined />} style={{ color: '#64748b', fontWeight: 600 }}>Importar</Button>
                        </Dropdown>
                        <Dropdown overlay={exportMenu} placement="bottomRight">
                            <Button type="text" icon={<DownloadOutlined />} style={{ color: '#64748b', fontWeight: 600 }}>Exportar</Button>
                        </Dropdown>
                    </div>
                </div>
            </div>
        </Card>
    );
}
